// Firebase configuration and initialization
import { initializeApp, getApps, FirebaseApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getMessaging, onMessage, Messaging } from "firebase/messaging";


const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase app (only once)
let app: FirebaseApp;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Initialize analytics only in browser
let analytics: ReturnType<typeof getAnalytics> | null = null;
if (typeof window !== "undefined") {
  analytics = getAnalytics(app);
}

// Get Firebase messaging instance (only in browser)
export const getMessagingInstance = (): Messaging | null => {
  if (typeof window === "undefined") {
    console.warn("Firebase messaging is only available in the browser");
    return null;
  }
  try {
    return getMessaging(app);
  } catch (error) {
    console.error("Error getting messaging instance:", error);
    return null;
  }
};

// Register the Firebase messaging service worker
export const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (!("serviceWorker" in navigator)) {
    console.log("Service workers are not supported");
    return null;
  }

  try {
    // Check if firebase-messaging-sw.js is already registered
    const existingRegistration = await navigator.serviceWorker.getRegistration("/firebase-messaging-sw.js");
    if (existingRegistration) {
      console.log("Firebase messaging service worker already registered");
      return existingRegistration;
    }

    // Register the Firebase messaging service worker
    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/",
    });
    console.log("Firebase messaging service worker registered:", registration);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    return registration;
  } catch (error) {
    console.error("Error registering service worker:", error);
    return null;
  }
};

// Listen for foreground messages - returns unsubscribe function
export const onMessageListener = (callback: (payload: any) => void): (() => void) => {
  const messaging = getMessagingInstance();
  if (!messaging) {
    console.warn("Messaging not available");
    // Return no-op unsubscribe function when messaging is not available
    return () => {
      console.warn("No messaging instance; unsubscribe is a no-op");
      // No cleanup needed
    };
  }

  const unsubscribe = onMessage(messaging, (payload) => {
    console.log("Message received in foreground:", payload);
    callback(payload);
  });

  return unsubscribe;
};

export default app;
